import { inject, Injectable } from '@angular/core';
import { AiTutorModeService } from 'src/app/services/ai-tutor-mode.service';
import { BehaviorSubject, combineLatest, Observable, Subscription, fromEvent, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
  ScribbleType,
  SessionSharedDataService,
  SessionView,
} from 'src/app/services/session-shared-data.service';
import { PanelLayoutService } from 'src/app/services/panel-layout.service';
import { filterNil } from '@ngneat/elf';
import { ObservableUtils } from 'src/app/utilities/ObservableUtils';
import { FlagsService, FLAGS } from 'src/app/services/flags.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';
import { DeviceAndBrowserDetectorService } from 'src/app/services/device-and-browser-detector.service';
import { SpaceBoardsService } from 'src/app/services/space-boards.service';
import { UiService } from 'src/app/services/ui.service';
import { SpaceRepository } from 'src/app/state/space.repository';
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import { KeyboardShortcutsCheckService } from 'src/app/services/keyboard-shortcuts-check.service';
import { ChildModeService } from 'src/app/services/child-mode.service';
import { ObserverModeService } from 'src/app/services/observer-mode.service';
import { BottomToolbarType } from '../utils/bottom-toolbar.types';
import { YCanvas } from '../../wb-canvas/fabric-utils';
import { Shapes } from '../utils/shapes-factory';
export const MIN_WIDTH_NEEDED_TO_SHOW_TOOLTIP_FROM_LEFT = 810;

const DEFAULT_MOUSE_BUFFER_INTERVAL = 30;

export enum PenTypeEnum {
  DRAW = 'draw',
  HIGHLIGHT = 'highlight',
  MAGIC_INK = 'magicInk',
}

// specify if keydown event has shift or not to avoid any conflict with
// other keyboard shortcuts (like shift + n for board navigation)
export enum KeyboardKeys {
  SPACE = 'space',
  POINTER = 'no_shift.v',
  PAN = 'no_shift.m',
  DRAW = 'no_shift.p',
  HIGHLIGHT = 'shift.p',
  MAGIC_INK = 'shift.l',
  ERASER = 'no_shift.e',
  DELETE = 'shift.delete',
  TEXT = 'no_shift.t',
  STICKY = 'no_shift.n',
  SHAPES = 'no_shift.s',
  LINE = 'no_shift.l',
  UPLOAD = 'no_shift.u',
}

export enum ToolbarCursorEnum {
  DEFAULT = 'default',
  POINTER = 'url(../../../assets/icons/wb-cursors/wb-pointer-v2.svg), default',
  DRAW = 'url(../../../../../assets/icons/wb-cursors/wb-draw-v2.svg) 5 16, crosshair',
  ERASER = 'url(../../../../../assets/icons/wb-cursors/wb-eraser-v2.svg) 5 16, crosshair',
  HIGHLIGHT = 'url(../../../../../assets/icons/wb-cursors/wb-highlighter-v2.svg) 2 16, crosshair',
  MAGIC_INK = 'url(../../../assets/icons/wb-cursors/wb-magicInk.svg) 4 18.5, crosshair',
  STICKY = 'url(../../../assets/icons/wb-cursors/wb-sticky-v2.svg) 5 16, move',
  PAN = 'url(../../../assets/icons/wb-cursors/wb-pan-v2.svg) 5 16, move',
  PAN_OBSERVER = 'url(../../../assets/icons/wb-cursors/wb-pan-v2-observer.svg) 5 16, move',
  CROSSHAIR = 'crosshair',
  TEXT = 'url(../../../assets/icons/wb-cursors/wb-text-v2.svg) 5 16, text',
  SHAPES = 'url(../../../assets/icons/wb-cursors/wb-shape-v2.svg) 11.5 11.5, crosshair',
  MOVE = 'move',
  ALL_SCROLL = 'all-scroll',
}

// These tools open menu when active, so we don't want to switch back to them when right click is released
const RIGHT_CLICK_EXCLUDED_TOOLS = [
  BottomToolbarType.TOOLBOX,
  BottomToolbarType.ZOOM,
  BottomToolbarType.UPLOAD,
];

export const ENABLED_TOOLS_ON_SPACE_OR_BOARD_LOCKED = [
  BottomToolbarType.PAN,
  BottomToolbarType.ZOOM,
];

/** Toolbar types allowed when child mode is active (pointer, pan, pen, eraser, empty for locked+led). */
export const TOOLS_ALLOWED_IN_CHILD_MODE: readonly BottomToolbarType[] = [
  BottomToolbarType.POINTER,
  BottomToolbarType.PAN,
  BottomToolbarType.PEN,
  BottomToolbarType.ERASER,
  BottomToolbarType.EMPTY,
];

/**
 * Toolbar types allowed when observer mode is active. Observers can only pan
 * (which is forced as the default tool) - and zoom actions.
 * EMPTY is included for the same locked/led transitions handled elsewhere.
 */
export const TOOLS_ALLOWED_IN_OBSERVER_MODE: readonly BottomToolbarType[] = [
  BottomToolbarType.PAN,
  BottomToolbarType.ZOOM,
  BottomToolbarType.EMPTY,
];

@Injectable({
  providedIn: 'root',
})
export class MainToolbarService {
  private readonly aiTutorMode = inject(AiTutorModeService);

  constructor(
    protected observerUtils: ObservableUtils,
    protected flagsService: FlagsService,
    protected spaceRepo: SpaceRepository,
    protected boardsService: SpaceBoardsService,
    protected UIService: UiService,
    protected deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    protected deviceDetectorService: DeviceDetectorService,
    private sessionSharedDataService: SessionSharedDataService,
    protected keyboardShortcutsCheckService: KeyboardShortcutsCheckService,
    protected childModeService: ChildModeService,
    protected panelLayoutService: PanelLayoutService,
    protected screenSizeService: ScreenSizeService,
    protected translate: TranslateService,
    protected observerModeService: ObserverModeService,
  ) {
    this.shouldActivatePanByDefault();
  }

  public readonly isOutsideTargetedSessionView$ =
    this.sessionSharedDataService.sessionView.current$.pipe(
      map((sessionView) => !this._targetToolbarViews.includes(sessionView.view)),
      distinctUntilChanged(),
    );

  private _targetToolbarViews = [SessionView.WHITEBOARD];
  private _canvas = new BehaviorSubject<YCanvas | null>(null);
  public readonly canvasReady$ = this._canvas.pipe(filterNil(), distinctUntilChanged());

  selectedToolbar = new BehaviorSubject<BottomToolbarType>(BottomToolbarType.POINTER);
  selectedToolbar$ = this.canvasReady$.pipe(
    switchMap(() =>
      this.selectedToolbar
        .asObservable()
        .pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true })),
    ),
  );

  readonly notifyActiveTool$ = this.selectedToolbar.asObservable().pipe(
    pairwise(),
    filter(([previous, current]) => previous === current),
    map(([_, current]) => current),
  );

  private _activePen: BehaviorSubject<PenTypeEnum> = new BehaviorSubject<PenTypeEnum>(
    PenTypeEnum.DRAW,
  );
  public readonly activePen$ = this._activePen.asObservable().pipe(
    distinctUntilChanged(),
    tap(() => {
      this.setToolCursor(this.activeToolType);
    }),
  );

  private _showMessagesSnackBar = new Subject<string | null>();
  public readonly showMessagesSnackBar$ = this._showMessagesSnackBar.asObservable();

  private _activeShape: BehaviorSubject<Shapes> = new BehaviorSubject<Shapes>(Shapes.RECTANGLE);
  public readonly activeShape$ = this._activeShape.asObservable().pipe(distinctUntilChanged());

  private _toolMenuOpen = new BehaviorSubject<BottomToolbarType | undefined>(undefined);
  public readonly anyMenuOpen$ = this._toolMenuOpen
    .asObservable()
    .pipe(map((toolbar) => Boolean(toolbar)));
  public readonly toolMenuOpen$ = this._toolMenuOpen.asObservable();

  /**
   * Stores a pending mouse event that should be consumed by the newly activated tool.
   * This is used when switching tools via right-click - the PAN tool needs
   * the initial mouseDown event to start panning correctly.
   */
  private _pendingEventForNewTool: {
    event: fabric.IEvent;
    eventName: 'mouseDown' | 'mouseUp';
  } | null = null;

  private deferredToolbar?: BottomToolbarType;
  protected mouseUpSubscription?: Subscription;
  protected mouseDownSubscription?: Subscription;
  protected keydownSubscription?: Subscription;
  protected keyupSubscription?: Subscription;

  private currentUserIsBeingLed = false;
  private _spaceOrBoardLocked = false;

  // to skip the board lock check for screen share annotations toolbar
  // so we don't disable tools when the current board is locked
  protected skipBoardLockCheck = false;

  public readonly spaceOrBoardLocked$ = combineLatest([
    this.spaceRepo.isCurrentUserHost$,
    this.spaceRepo.activeSpaceLocked$,
    this.boardsService.activeSpaceSelectedBoardLocked$,
  ]).pipe(
    map(([isHost, spaceLocked, boardLocked]) => {
      this._spaceOrBoardLocked =
        !isHost && (spaceLocked || (boardLocked && !this.skipBoardLockCheck));
      return this._spaceOrBoardLocked;
    }),
  );

  public readonly isSpaceLocked$ = combineLatest([
    this.spaceRepo.isCurrentUserHost$,
    this.spaceRepo.activeSpaceLocked$,
  ]).pipe(map(([isHost, spaceLocked]) => !isHost && spaceLocked));

  setSelectedToolbar(toolbar: BottomToolbarType) {
    // Observers may only use the pan tool. Force PAN if any other tool is requested.
    if (
      this.observerModeService.isObserverModeActive() &&
      !TOOLS_ALLOWED_IN_OBSERVER_MODE.includes(toolbar)
    ) {
      this.selectedToolbar.next(BottomToolbarType.PAN);
      return;
    }

    if (this._spaceOrBoardLocked) {
      // when user is being led & space or board is locked, the only allowed tool is zoom
      if (this.currentUserIsBeingLed && toolbar !== BottomToolbarType.ZOOM) {
        this.selectedToolbar.next(BottomToolbarType.EMPTY);
        this.setToolCursor(BottomToolbarType.EMPTY);
        return;
      }

      if (!ENABLED_TOOLS_ON_SPACE_OR_BOARD_LOCKED.includes(toolbar)) {
        this.selectedToolbar.next(BottomToolbarType.PAN);
        return;
      }
    }

    // set the new active tool
    this.selectedToolbar.next(toolbar);
  }

  setIsAnySubMenuOpen(isOpen: boolean, sourceTool: BottomToolbarType) {
    if (isOpen && this.activeToolName === sourceTool) {
      this._toolMenuOpen.next(sourceTool);
      return;
    }

    if (!isOpen && this._toolMenuOpen.getValue() === sourceTool) {
      this._toolMenuOpen.next(undefined);
    }
  }

  getScribbleType(): ScribbleType {
    if (this.activeToolName === BottomToolbarType.PEN) {
      if (this.activePen === PenTypeEnum.DRAW) {
        return ScribbleType.DRAW;
      }

      if (this.activePen === PenTypeEnum.HIGHLIGHT) {
        return ScribbleType.HIGHLIGHT;
      }
    }
    return ScribbleType.NOT_SCRIBBLE;
  }

  setCurrentUserIsBeingLed(isBeingLed: boolean) {
    this.currentUserIsBeingLed = isBeingLed;
  }

  setCanvas(canvas: YCanvas | null) {
    this._canvas.next(canvas);
  }

  /**
   * Reset service state to initial values
   * Call this when a new session is created to ensure clean state
   */
  resetServiceState(): void {
    // Reset toolbar selection to default
    this.selectedToolbar.next(BottomToolbarType.POINTER);

    // Reset pen type to default
    this._activePen.next(PenTypeEnum.DRAW);

    // Reset sub menu state
    this._toolMenuOpen.next(undefined);

    // Clear any deferred toolbar
    this.deferredToolbar = undefined;

    // Clear pending event
    this._pendingEventForNewTool = null;

    this.unsubscribeFromEventListeners();

    // Reapply default pan setting for mobile devices
    this.shouldActivatePanByDefault();
  }

  private getToastMessageForType(type: BottomToolbarType): string {
    if (type === BottomToolbarType.PAN) {
      return this.translate.instant('Moving is disabled when someone is leading');
    }
    if (type === BottomToolbarType.GALLERY_MODE) {
      return this.translate.instant('You cannot go to the gallery view while the host is leading');
    }

    if (this.spaceRepo.activeSpace?.isLocked) {
      return this.translate.instant('Editing is disabled because this Space is locked');
    }

    return this.translate.instant('Editing is disabled because this Board is locked');
  }

  private _timeoutId?: NodeJS.Timer;
  showMessageSnackBar(type: BottomToolbarType) {
    this._showMessagesSnackBar.next(this.getToastMessageForType(type));
    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
    }

    this._timeoutId = modifiedSetTimeout(() => {
      this._showMessagesSnackBar.next(null);
    }, 5000);
  }

  get isAnySubMenuOpen(): boolean {
    return Boolean(this._toolMenuOpen.getValue());
  }

  get activeToolType() {
    return this.selectedToolbar.value;
  }

  get canvasMouseMoveBuffered$() {
    return this.canvasReady$.pipe(
      switchMap((canvas) =>
        this.observerUtils.bufferedObservableOf(canvas.onMouseMove$, this.mouseBufferInterval),
      ),
    );
  }

  public switchToToolbar(toolName: string | BottomToolbarType) {
    this.switchToToolbarV2(toolName);
  }

  public switchToToolbarV2(toolName: string | BottomToolbarType) {
    this.setSelectedToolbar(toolName as BottomToolbarType);

    if (this._toolMenuOpen.getValue() && toolName !== this._toolMenuOpen.getValue()) {
      this._toolMenuOpen.next(undefined);
    }
  }

  public setupKeydownLisnters(): void {
    this.keydownSubscription?.unsubscribe();

    if (!this.isShortcutKeysEnabled) {
      return;
    }

    this.keydownSubscription = this.keyDownShortcutKeys$.subscribe((key: KeyboardKeys) => {
      if (!this.childModeService.isShortcutEnabledInChildMode(key)) {
        return;
      }
      // Observers have no tool to switch to; suppress all tool shortcuts (pan is always active).
      if (this.observerModeService.isObserverModeActive()) {
        return;
      }
      switch (key) {
        case KeyboardKeys.POINTER:
          this.switchToToolbar(BottomToolbarType.POINTER);
          break;
        case KeyboardKeys.PAN:
          if (!this.currentUserIsBeingLed && this.activeToolName !== BottomToolbarType.PAN) {
            this.switchToToolbar(BottomToolbarType.PAN);
          }
          break;
        case KeyboardKeys.SPACE:
          if (!this.currentUserIsBeingLed && this.activeToolName !== BottomToolbarType.PAN) {
            this.deferredToolbar = this.activeToolName;
            this.switchToToolbar(BottomToolbarType.PAN);
          }
          break;
        case KeyboardKeys.DRAW:
          if (
            this.activeToolName !== BottomToolbarType.PEN ||
            this.activePen === PenTypeEnum.DRAW
          ) {
            this.switchToToolbar(BottomToolbarType.PEN);
          }

          this.activePen = PenTypeEnum.DRAW;
          break;
        case KeyboardKeys.HIGHLIGHT:
          if (
            this.activeToolName !== BottomToolbarType.PEN ||
            this.activePen === PenTypeEnum.HIGHLIGHT
          ) {
            this.switchToToolbar(BottomToolbarType.PEN);
          }

          this.activePen = PenTypeEnum.HIGHLIGHT;
          break;
        case KeyboardKeys.MAGIC_INK:
          // No laser pointer in the Luna experience.
          if (
            !this.flagsService.isFlagEnabled(FLAGS.ENABLE_LASER_POINTER_TOOL) ||
            this.aiTutorMode.enabled
          ) {
            break;
          }
          if (
            this.activeToolName !== BottomToolbarType.PEN ||
            this.activePen === PenTypeEnum.MAGIC_INK
          ) {
            this.switchToToolbar(BottomToolbarType.PEN);
          }

          this.activePen = PenTypeEnum.MAGIC_INK;
          break;
        case KeyboardKeys.ERASER:
          this.switchToToolbar(BottomToolbarType.ERASER);
          break;
        case KeyboardKeys.DELETE:
          this.switchToToolbar(BottomToolbarType.ERASER);
          break;
        case KeyboardKeys.TEXT:
          if (this.textBoxEnabled) {
            this.switchToToolbar(BottomToolbarType.TEXT);
          }
          break;
        case KeyboardKeys.UPLOAD:
          // Don't allow upload in gallery view or when host is leading
          const currentSessionView = this.sessionSharedDataService.sessionView.getSessionView();
          if (currentSessionView !== SessionView.GALLERY_VIEW && !this.currentUserIsBeingLed) {
            this.switchToToolbar(BottomToolbarType.UPLOAD);
          }
          break;
        case KeyboardKeys.STICKY:
          this.switchToToolbar(BottomToolbarType.STICKY);
          break;
        case KeyboardKeys.SHAPES:
          this.activeShape = Shapes.RECTANGLE;
          this.switchToToolbar(BottomToolbarType.SHAPES);
          break;
        case KeyboardKeys.LINE:
          this.activeShape = Shapes.LINE;
          this.switchToToolbar(BottomToolbarType.SHAPES);
          break;
      }
    });
  }

  set activeShape(value: Shapes) {
    this._activeShape.next(value);
  }

  get activeShape() {
    return this._activeShape.value;
  }

  public setupKeyupListeners(): void {
    this.keyupSubscription?.unsubscribe();
    this.keyupSubscription = this.keyUpShortcutKeys$.subscribe((key: KeyboardKeys) => {
      if (key === KeyboardKeys.SPACE && this.deferredToolbar) {
        this.switchToToolbar(this.deferredToolbar);
        this.deferredToolbar = undefined;
      }
    });
  }

  public setUpMouseUpListener(): void {
    this.mouseUpSubscription = this.fabricCanvas?.onMouseUp$.subscribe((e: fabric.IEvent) => {
      // If the space or board is locked, don't allow any mouse up events
      if (this._spaceOrBoardLocked) {
        return;
      }

      const mouseEvent = e.e as MouseEvent;

      // when both mouse button were clicked and the right one is released
      if (mouseEvent.button === 2 && mouseEvent.buttons === 1) {
        // discardActiveObject to prevent the canvas from being unresponsive.
        this.fabricCanvas?.discardActiveObject();
        return;
      }
      // Recover from switching to pan toolbar after releasing the right click
      if (
        mouseEvent.button === 2 &&
        this.activeToolName === BottomToolbarType.PAN &&
        this.deferredToolbar !== BottomToolbarType.PAN
      ) {
        this.switchToToolbar(this.deferredToolbar || BottomToolbarType.POINTER);
      }
    });
  }

  public setUpMouseDownListener(): void {
    this.mouseDownSubscription?.unsubscribe();
    this.mouseDownSubscription = this.fabricCanvas?.onMouseDown$.subscribe((e: fabric.IEvent) => {
      // If the space or board is locked, don't allow any mouse down events
      if (this._spaceOrBoardLocked) {
        return;
      }

      const mouseEvent = e.e as MouseEvent;

      // On right click switch to pan before passing mouse down event to the active toolbar (Pan in this case)
      // but not when both right and left buttons are clicked
      const onlyRightButtonClicked = mouseEvent.button === 2 && mouseEvent.buttons !== 3;
      if (onlyRightButtonClicked) {
        if (!RIGHT_CLICK_EXCLUDED_TOOLS.includes(this.activeToolType)) {
          this.deferredToolbar = this.activeToolType;
        }

        if (!this.currentUserIsBeingLed) {
          // Store the mouseDown event for the PAN tool to consume on activation
          this._pendingEventForNewTool = { event: e, eventName: 'mouseDown' };
          this.switchToToolbar(BottomToolbarType.PAN);
        }
      }
    });
  }

  public unsubscribeFromEventListeners(): void {
    this.mouseUpSubscription?.unsubscribe();
    this.mouseDownSubscription?.unsubscribe();
    this.keydownSubscription?.unsubscribe();
    this.keyupSubscription?.unsubscribe();
  }

  private shouldActivatePanByDefault() {
    if (
      this.UIService.isMobile.getValue() ||
      this.deviceAndBrowserDetectorService.isTabletDevice() ||
      this.deviceDetectorService.isMobile()
    ) {
      this.setSelectedToolbar(BottomToolbarType.PAN);
    }
  }

  public setToolCursor(selectedTool: BottomToolbarType): void {
    let cursor = ToolbarCursorEnum.DEFAULT;
    switch (selectedTool) {
      case BottomToolbarType.EMPTY:
      case BottomToolbarType.POINTER:
        cursor = ToolbarCursorEnum.POINTER;
        break;
      case BottomToolbarType.PEN:
        if (this.activePen === PenTypeEnum.DRAW) {
          cursor = ToolbarCursorEnum.DRAW;
        } else if (this.activePen === PenTypeEnum.HIGHLIGHT) {
          cursor = ToolbarCursorEnum.HIGHLIGHT;
        } else {
          cursor = ToolbarCursorEnum.MAGIC_INK;
        }
        break;
      case BottomToolbarType.ERASER:
        cursor = ToolbarCursorEnum.ERASER;
        break;
      case BottomToolbarType.STICKY:
        cursor = ToolbarCursorEnum.STICKY;
        break;
      case BottomToolbarType.TEXT:
        cursor = ToolbarCursorEnum.TEXT;
        break;
      case BottomToolbarType.PAN:
        cursor = this.observerModeService.isObserverModeActive()
          ? ToolbarCursorEnum.PAN_OBSERVER
          : ToolbarCursorEnum.PAN;
        break;
      case BottomToolbarType.SHAPES:
        cursor = ToolbarCursorEnum.SHAPES;
        break;
      default:
        cursor = ToolbarCursorEnum.POINTER;
        break;
    }

    if (this.fabricCanvas) {
      this.fabricCanvas.defaultCursor = cursor;
      this.fabricCanvas.setCursor(cursor);
      this.fabricCanvas.hoverCursor = cursor;
      if (this.activeToolType === BottomToolbarType.PEN) {
        this.fabricCanvas.freeDrawingCursor = cursor;
      }

      this.fabricCanvas.requestRenderAll();
    }
  }

  setSkipMultiTouchHandling(selectedTool: BottomToolbarType): void {
    (this.fabricCanvas as any).skipMultiTouchHandling = [
      BottomToolbarType.TEXT,
      BottomToolbarType.STICKY,
      BottomToolbarType.PAN,
    ].includes(selectedTool);
  }

  private _skipKeyboardEvent = false;
  private mapKeyboardEvent$ = (source: Observable<Event>) =>
    source.pipe(
      filter((e: Event) => {
        const event = e as KeyboardEvent;
        const isSessionViewAllowedForShortcuts = this.isTargetToolbarView();

        if (this.panelLayoutService.anyPanelOpenedAsBottomSheet) {
          return false;
        }
        // Use unified shortcut skip check (includes session view check)
        if (
          this._skipKeyboardEvent ||
          this.keyboardShortcutsCheckService.shouldSkipKeyboardShortcutsForSessionView(
            isSessionViewAllowedForShortcuts,
            event,
          )
        ) {
          return false;
        }

        // as no tool has alt / ctrl / meta key as shortcut, we can skip the event if it has alt / ctrl / meta key
        if (event.altKey || event.ctrlKey || event.metaKey || event.repeat) {
          return false;
        }

        // skip if key is shift
        if (event.key === 'Shift') {
          return false;
        }

        if (this._spaceOrBoardLocked) {
          return false;
        }

        return true;
      }),
      map((e: Event) => {
        const event = e as KeyboardEvent;
        const isSpace = event.code.toLowerCase() === 'space';
        if (isSpace) {
          return 'space' as KeyboardKeys;
        }

        const shift_key = event.shiftKey ? 'shift' : 'no_shift';
        return `${shift_key}.${event.key}`.toLowerCase() as KeyboardKeys;
      }),
      filter((key: KeyboardKeys) => Object.values(KeyboardKeys).includes(key)),
    );

  public isTargetToolbarView(): boolean {
    const currentSessionView = this.sessionSharedDataService.sessionView.getSessionView();
    return this._targetToolbarViews.includes(currentSessionView);
  }

  public shouldCloseMenusOnSessionViewChange(sessionView: SessionView): boolean {
    return !this._targetToolbarViews.includes(sessionView);
  }

  public readonly keyDownShortcutKeys$ = fromEvent(document, 'keydown').pipe(
    this.mapKeyboardEvent$,
  );

  public readonly keyUpShortcutKeys$ = fromEvent(document, 'keyup').pipe(this.mapKeyboardEvent$);

  /**
   * Returns and clears the pending event for newly activated tool.
   * This should be called by tools (like PAN) that need the initial
   * mouseDown event when switched via right-click.
   */
  consumePendingEvent(): { event: fabric.IEvent; eventName: 'mouseDown' | 'mouseUp' } | null {
    const event = this._pendingEventForNewTool;
    this._pendingEventForNewTool = null;
    return event;
  }

  public setTargetToolbarViews(sessionViews: SessionView[]) {
    this._targetToolbarViews = sessionViews;
  }

  public setSkipKeyboardEvent(value: boolean) {
    this._skipKeyboardEvent = value;
  }

  public skipUndoRedo(): boolean {
    return this._spaceOrBoardLocked;
  }

  set activePen(value: PenTypeEnum) {
    this._activePen.next(value);
  }

  get activeToolName(): BottomToolbarType | undefined {
    return this.activeToolType;
  }

  get rtEventToolbar() {
    if (this.activeToolName === BottomToolbarType.PEN) {
      if (this.activePen === PenTypeEnum.DRAW) {
        return 'draw';
      }

      if (this.activePen === PenTypeEnum.HIGHLIGHT) {
        return 'highlight';
      }

      return 'magicInk';
    }

    if (this.activeToolName === BottomToolbarType.ERASER) {
      return 'erase';
    }

    return this.activeToolName || BottomToolbarType.POINTER;
  }

  get activePen() {
    return this._activePen.value;
  }

  get canvasMouseDown$() {
    return this.canvasReady$.pipe(
      switchMap((canvas) => canvas.onMouseDown$ as Observable<fabric.IEvent>),
    );
  }

  get canvasMouseUp$() {
    return this.canvasReady$.pipe(
      switchMap((canvas) => canvas.onMouseUp$ as Observable<fabric.IEvent>),
    );
  }

  get canvasMouseMove$() {
    return this.canvasReady$.pipe(
      switchMap((canvas) => canvas.onMouseMove$ as Observable<fabric.IEvent>),
    );
  }

  get mouseBufferInterval() {
    if (this.flagsService.featureFlagsVariables.event_buffering.send_toolbar_buffer_ms) {
      return this.flagsService.featureFlagsVariables.event_buffering
        .send_toolbar_buffer_ms as number;
    }
    return DEFAULT_MOUSE_BUFFER_INTERVAL;
  }

  get isShortcutKeysEnabled() {
    return this.flagsService.isFlagEnabled(FLAGS.SPACES_TOOLBAR_SHORTCUTS);
  }

  get textBoxEnabled() {
    // Always on in the Luna experience, whatever the flag says.
    return this.flagsService.isFlagEnabled(FLAGS.WB_TEXT_BOX) || this.aiTutorMode.enabled;
  }

  /**
   * Single source of truth for checking if keyboard shortcuts should be skipped.
   * This method consolidates all the common checks that all shortcuts need:
   * - isDisableKeyEvent
   * - isInputArea
   * - is textbox/item in edit mode
   * - is target app root
   *
   * @param event - Optional keyboard event to check the target
   * @param target - Optional HTML element target (if not provided, will use event.target)
   * @returns true if shortcuts should be skipped
   */
  shouldSkipKeyboardShortcuts(event?: KeyboardEvent, target?: HTMLElement): boolean {
    // Delegate to KeyboardShortcutsCheckService to avoid making this service a central dependency
    return this.keyboardShortcutsCheckService.shouldSkipKeyboardShortcuts(event, target);
  }

  get fabricCanvas(): YCanvas | null {
    return this._canvas.getValue();
  }

  get spaceOrBoardLocked(): boolean {
    return this._spaceOrBoardLocked;
  }

  get isDrawingTool(): boolean {
    return [
      BottomToolbarType.PEN,
      BottomToolbarType.ERASER,
      BottomToolbarType.TEXT,
      BottomToolbarType.STICKY,
      BottomToolbarType.SHAPES,
    ].includes(this.activeToolType);
  }
}
